import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView, TextInput, Alert, ActivityIndicator, Image, Keyboard, Platform, KeyboardAvoidingView, PanResponder } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { UserProfile } from '../../types/profile';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, ProfileUpdateData } from '../../lib/api/users';
import { storage } from '../../utils/storage';
import { apiBaseUrl } from '../../config/env';
import ThemedText from '../common/ThemedText';
import { CustomPicker } from '../common/CustomPicker';
import { DatePicker } from '../common/DatePicker';
import { getCountries, getCitiesForCountry } from '../../data/countriesAndCities';

const CITIES = [
  'Bangalore', 'Mumbai', 'Delhi', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata',
  'Ahmedabad', 'Surat', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore',
  'Thane', 'Bhopal', 'Visakhapatnam', 'Vadodara', 'Ghaziabad', 'Ludhiana',
  'Agra', 'Nashik', 'Faridabad', 'Meerut', 'Rajkot', 'Varanasi', 'Srinagar',
  'Amritsar', 'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore', 'Vijayawada',
  'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Guwahati', 'Chandigarh',
];

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Retail',
  'Manufacturing',
  'Real Estate',
  'Consulting',
  'Marketing',
  'Media & Entertainment',
  'Hospitality',
  'Transportation',
  'Energy',
  'Agriculture',
  'Construction',
  'Legal',
  'Government',
  'Non-Profit',
  'Other',
];

const GENDERS = [
  'Male',
  'Female',
  'Non-Binary',
  'Prefer not to say',
  'Other',
];

const PROFESSIONS = [
  'Software Engineer',
  'Product Designer',
  'Product Manager',
  'Marketing Manager',
  'Sales Manager',
  'Business Analyst',
  'Data Scientist',
  'DevOps Engineer',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'UX Designer',
  'UI Designer',
  'Content Writer',
  'Project Manager',
  'HR Manager',
  'Finance Manager',
  'Operations Manager',
  'Consultant',
  'Entrepreneur',
  'Freelancer',
  'Other',
];

interface PersonalProfileProps {
  profile: UserProfile;
  isOwnProfile: boolean;
  onEdit?: () => void;
  isEditing: boolean;
  onEditPress: () => void;
  onSavePress: (data: ProfileUpdateData) => void;
  onCancelPress: () => void;
  isSaving: boolean;
}

type TabType = 'contact' | 'personal' | 'business';

const PersonalProfile: React.FC<PersonalProfileProps> = ({ 
  profile, 
  isOwnProfile, 
  onEdit,
  isEditing,
  onEditPress,
  onSavePress,
  onCancelPress,
  isSaving
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [activeTab, setActiveTab] = useState<TabType>('contact');
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [photoChanged, setPhotoChanged] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  // Form state
  const [formData, setFormData] = useState<ProfileUpdateData>({});
  const [originalFormData, setOriginalFormData] = useState<ProfileUpdateData>({});

  // Get available cities based on selected country
  const availableCities = useMemo(() => {
    if (!formData.country) {
      return ['Other'];
    }
    return getCitiesForCountry(formData.country);
  }, [formData.country]);

  // Swipe gesture handler for tab switching
  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (evt, gestureState) => {
        // Only respond to horizontal swipes (not vertical scrolling)
        return Math.abs(gestureState.dx) > Math.abs(gestureState.dy) && Math.abs(gestureState.dx) > 20;
      },
      onPanResponderRelease: (evt, gestureState) => {
        // Check for unsaved changes
        const hasChanges = photoChanged || JSON.stringify(formData) !== JSON.stringify(originalFormData);
        if (isEditing && hasChanges) {
          return; // Don't allow swipe if there are unsaved changes
        }

        const tabs: TabType[] = ['contact', 'personal', 'business'];
        const currentIndex = tabs.indexOf(activeTab);
        
        // Swipe left = next tab, Swipe right = previous tab
        if (gestureState.dx < -50 && currentIndex < tabs.length - 1) {
          setActiveTab(tabs[currentIndex + 1]);
        } else if (gestureState.dx > 50 && currentIndex > 0) {
          setActiveTab(tabs[currentIndex - 1]);
        }
      },
    })
  ).current;

  // Handle country change - reset city when country changes
  const handleCountryChange = (country: string) => {
    setFormData(prev => ({
      ...prev,
      country: country,
      city: '', // Reset city when country changes
    }));
  };

  useEffect(() => {
    // Initialize form data from profile prop (use profile for both own and other users)
    const profileData = isOwnProfile ? user : profile;
    if (profileData) {
      // Initialize profile photo from profile.photo field
      setProfilePhoto((profileData as any).photo || null);
      const initialData = {
        name: (profileData as any).name || '',
        email: (profileData as any).email || '',
        dateOfBirth: (profileData as any).dateOfBirth || '',
        company: (profileData as any).company || '',
        designation: (profileData as any).designation || (profileData as any).profession || '',
        gender: (profileData as any).gender || '',
        country: (profileData as any).country || '',
        state: (profileData as any).state || '',
        city: (profileData as any).city || '',
        industry: (profileData as any).industry || '',
        profession: (profileData as any).profession || '',
        // Personal
        spouse: (profileData as any).spouse || '',
        children: (profileData as any).children || '',
        animals: (profileData as any).animals || '',
        hobbies: (profileData as any).hobbies || '',
        activityOfInterest: (profileData as any).activityOfInterest || '',
        cityOfResidence: (profileData as any).cityOfResidence || '',
        burningDesire: (profileData as any).burningDesire || '',
        somethingNoOneKnows: (profileData as any).somethingNoOneKnows || '',
        keyToSuccesses: (profileData as any).keyToSuccesses || '',
        personnelGoals: (profileData as any).personnelGoals || '',
        personnelAccomplishments: (profileData as any).personnelAccomplishments || '',
        personnelInterests: (profileData as any).personnelInterests || '',
        personnelNetwork: (profileData as any).personnelNetwork || '',
        personnelSkills: (profileData as any).personnelSkills || '',
        // Business
        businessName: (profileData as any).businessName || '',
        businessLocation: (profileData as any).businessLocation || '',
        previousTypeOfJobs: (profileData as any).previousTypeOfJobs || '',
        businessGoals: (profileData as any).businessGoals || '',
        businessAccomplishments: (profileData as any).businessAccomplishments || '',
        businessInterests: (profileData as any).businessInterests || '',
        businessNetwork: (profileData as any).businessNetwork || '',
        businessSkills: (profileData as any).businessSkills || '',
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
    }
  }, [profile, user, isOwnProfile]);

  useEffect(() => {
    console.log('🧾 [PersonalProfile] Edit view state changed', {
      isEditing,
      activeTab,
    });
  }, [isEditing, activeTab]);

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, (event) => {
      const height = event?.endCoordinates?.height ?? 320;
      console.log('⌨️ [PersonalProfile] Keyboard visible', {
        height: event?.endCoordinates?.height,
        duration: event?.duration,
        easing: event?.easing,
        isEditing,
        activeTab,
      });
      setKeyboardVisible(true);
      setKeyboardOffset(height);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      console.log('⌨️ [PersonalProfile] Keyboard hidden', {
        isEditing,
        activeTab,
      });
      setKeyboardVisible(false);
      setKeyboardOffset(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [isEditing, activeTab]);

  const pickImage = async () => {
    try {
      // Note: On Android 13+, launchImageLibraryAsync uses the system Photo Picker
      // which doesn't need READ_MEDIA_* permissions.
      // Requesting these permissions causes Google Play policy violations.
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
        presentationStyle: ImagePicker.UIImagePickerPresentationStyle.FULL_SCREEN,
        selectionLimit: 1,
        exif: false,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        setProfilePhoto(result.assets[0].uri);
        setPhotoChanged(true);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const removePhoto = () => {
    Alert.alert(
      'Remove Photo',
      'Are you sure you want to remove your profile photo?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            setProfilePhoto(null);
            setPhotoChanged(true);
          },
        },
      ]
    );
  };

  const uploadImageToServer = async (uri: string): Promise<string> => {
    console.log('🔄 Starting image upload...', uri);
    
    const token = await storage.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }

    // Create form data
    const formData = new FormData();
    const filename = uri.split('/').pop() || 'photo.jpg';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    console.log('📁 File info:', { filename, type });

    // Handle web differently - need to fetch the blob and create a File
    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();
      const file = new File([blob], filename, { type });
      formData.append('image', file);
    } else {
      formData.append('image', {
        uri,
        name: filename,
        type,
      } as any);
    }

    console.log('☁️ Uploading to:', `${apiBaseUrl}/api/upload/image`);

    // Upload to backend
    // IMPORTANT: Don't set Content-Type header - let browser set it with boundary for multipart
    const response = await fetch(`${apiBaseUrl}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    console.log('📥 Upload response:', data);
    
    if (!data.success || !data.url) {
      throw new Error(data.message || 'Failed to upload image');
    }

    console.log('✅ Upload successful! URL:', data.url);
    return data.url;
  };

  const renderDateField = (
    label: string,
    fieldKey: keyof ProfileUpdateData,
    icon: string,
    required: boolean = false
  ) => {
    const fieldValue = formData[fieldKey];
    const value = typeof fieldValue === 'string' ? fieldValue : '';
    const dateValue = value ? new Date(value) : undefined;

    const handleDateChange = (date: Date | undefined) => {
      if (date) {
        const formattedDate = date.toISOString().split('T')[0];
        setFormData(prev => ({ ...prev, [fieldKey]: formattedDate }));
      } else {
        setFormData(prev => ({ ...prev, [fieldKey]: '' }));
      }
    };

    const formatDisplayDate = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name={icon as any} size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {label}{required && ' *'}
          </ThemedText>
        </View>
        {isEditing ? (
          <DatePicker
            value={dateValue}
            onChange={handleDateChange}
            label=""
            maximumDate={new Date()}
          />
        ) : (
          <ThemedText style={[styles.fieldValue, { color: value ? theme.text : theme.textTertiary }]}>
            {value ? (() => {
              const date = new Date(value);
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              // Show full date for own profile, hide year for others
              if (isOwnProfile) {
                return value; // Show full date YYYY-MM-DD
              }
              return `${monthNames[date.getMonth()]} ${date.getDate()}`;
            })() : 'Not specified'}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderField = (
    label: string, 
    fieldKey: keyof ProfileUpdateData,
    icon: string, 
    required: boolean = false
  ) => {
    const fieldValue = formData[fieldKey];
    const value = typeof fieldValue === 'string' ? fieldValue : '';
    
    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name={icon as any} size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {label}{required && ' *'}
          </ThemedText>
        </View>
        {isEditing ? (
          <TextInput
            style={[styles.textInput, { 
              color: theme.text, 
              backgroundColor: theme.background,
              borderColor: theme.border
            }]}
            value={value}
            onChangeText={(text) => setFormData(prev => ({ ...prev, [fieldKey]: text }))}
            placeholder="Not specified"
            placeholderTextColor={theme.textTertiary}
          />
        ) : (
          <ThemedText style={[styles.fieldValue, { color: value ? theme.text : theme.textTertiary }]}>
            {value || 'Not specified'}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderPickerField = (
    label: string,
    fieldKey: keyof ProfileUpdateData,
    icon: string,
    options: string[],
    required: boolean = false,
    searchable: boolean = false
  ) => {
    const fieldValue = formData[fieldKey];
    const value = typeof fieldValue === 'string' ? fieldValue : '';
    
    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name={icon as any} size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {label}{required && ' *'}
          </ThemedText>
        </View>
        {isEditing ? (
          <View style={styles.pickerWrapper}>
            <CustomPicker
              selectedValue={value}
              onValueChange={(val) => setFormData(prev => ({ ...prev, [fieldKey]: val || '' }))}
              items={options.map((option) => ({
                label: option,
                value: option,
              }))}
              placeholder="Select an option"
              searchable={searchable}
              searchPlaceholder={searchable ? `Search ${label.toLowerCase()}` : undefined}
            />
          </View>
        ) : (
          <ThemedText style={[styles.fieldValue, { color: value ? theme.text : theme.textTertiary }]}>
            {value || 'Not specified'}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderCountryField = () => {
    const value = formData.country || '';
    
    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name="globe-outline" size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            Country *
          </ThemedText>
        </View>
        {isEditing ? (
          <View style={styles.pickerWrapper}>
            <CustomPicker
              selectedValue={value}
              onValueChange={(val) => handleCountryChange(val || '')}
              items={getCountries().map((country) => ({
                label: country,
                value: country,
              }))}
              placeholder="Select your country"
              searchable
              searchPlaceholder="Search countries"
            />
          </View>
        ) : (
          <ThemedText style={[styles.fieldValue, { color: value ? theme.text : theme.textTertiary }]}>
            {value || 'Not specified'}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderCityField = () => {
    const value = formData.city || '';
    
    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name="navigate-outline" size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            City *
          </ThemedText>
        </View>
        {isEditing ? (
          <View style={styles.pickerWrapper}>
            <CustomPicker
              selectedValue={value}
              onValueChange={(val) => setFormData(prev => ({ ...prev, city: val || '' }))}
              items={availableCities.map((city) => ({
                label: city,
                value: city,
              }))}
              placeholder={formData.country ? 'Select your city' : 'Select country first'}
              searchable
              searchPlaceholder="Search cities"
              disabled={!formData.country}
            />
          </View>
        ) : (
          <ThemedText style={[styles.fieldValue, { color: value ? theme.text : theme.textTertiary }]}>
            {value || 'Not specified'}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderTabButton = (tab: TabType, label: string) => {
    const isActive = activeTab === tab;
    return (
      <TouchableOpacity
        key={tab}
        style={[
          styles.tabButton,
          isActive && styles.tabButtonActive,
          { borderBottomColor: isActive ? theme.accent : 'transparent' }
        ]}
        onPress={() => {
          if (isEditing) {
            // Check if there are actual unsaved changes
            const hasChanges = photoChanged || JSON.stringify(formData) !== JSON.stringify(originalFormData);
            if (hasChanges) {
              Alert.alert('Unsaved Changes', 'Please save or cancel your changes first');
              return;
            }
          }
          setActiveTab(tab);
        }}
        activeOpacity={0.7}
      >
        <ThemedText
          style={[
            styles.tabText,
            { color: isActive ? theme.accent : isEditing ? theme.textTertiary : theme.textSecondary },
            isActive && styles.tabTextActive
          ]}
        >
          {label}
        </ThemedText>
      </TouchableOpacity>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'contact':
        return (
          <View style={styles.tabContent}>
            {/* Profile Picture */}
            <View style={styles.profilePhotoSection}>
              <View style={[styles.largeProfilePhoto, { backgroundColor: theme.border }]}>
                {profilePhoto ? (
                  <>
                    {console.log('🎨 [PersonalProfile] Rendering photo with URI:', profilePhoto)}
                    <Image 
                      source={{ uri: profilePhoto }} 
                      style={styles.profileImage}
                      onError={(error) => {
                        console.error('❌ [PersonalProfile] Image load error:', error.nativeEvent);
                        console.error('❌ [PersonalProfile] Failed URI:', profilePhoto);
                      }}
                      onLoad={() => console.log('✅ [PersonalProfile] Image loaded successfully')}
                    />
                  </>
                ) : (
                  <>
                    {console.log('👤 [PersonalProfile] No photo, showing icon')}
                    <Ionicons name="person" size={60} color={theme.textSecondary} />
                  </>
                )}
              </View>
              {isOwnProfile && isEditing && (
                <View style={styles.photoActions}>
                  <TouchableOpacity
                    style={[styles.photoActionButton, { backgroundColor: theme.accent }]}
                    onPress={pickImage}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="camera" size={16} color="#FFFFFF" />
                    <ThemedText style={styles.photoActionText}>Upload Photo</ThemedText>
                  </TouchableOpacity>
                  {profilePhoto && (
                    <TouchableOpacity
                      style={[styles.photoActionButton, { backgroundColor: '#EF4444' }]}
                      onPress={removePhoto}
                      activeOpacity={0.7}
                    >
                      <Ionicons name="trash-outline" size={16} color="#FFFFFF" />
                      <ThemedText style={styles.photoActionText}>Remove</ThemedText>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>

            {/* Contact Fields */}
            {renderField('Name', 'name', 'person-outline', true)}
            {renderField('Email', 'email', 'mail-outline', false)}
            {renderDateField('Date of Birth', 'dateOfBirth', 'calendar-outline', true)}
            <View style={styles.field}>
              <View style={styles.fieldHeader}>
                <Ionicons name="call-outline" size={18} color={theme.textSecondary} />
                <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                  Phone Number
                </ThemedText>
              </View>
              <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                {(() => {
                  const phone = (isOwnProfile ? user?.phoneNumber : (profile as any)?.phoneNumber);
                  if (!phone) return 'Not specified';
                  // Show full phone for own profile, mask for others
                  if (isOwnProfile) {
                    return phone; // Show full phone number
                  }
                  // Mask phone number: show first 2 and last 2 digits
                  if (phone.length > 4) {
                    return `${phone.substring(0, 2)}${'*'.repeat(phone.length - 4)}${phone.substring(phone.length - 2)}`;
                  }
                  return phone;
                })()}
              </ThemedText>
            </View>
            {renderPickerField('Industry', 'industry', 'business-outline', INDUSTRIES, true)}
            {renderField('Company', 'company', 'briefcase-outline', false)}
            {renderPickerField('Designation', 'designation', 'ribbon-outline', PROFESSIONS, true, true)}
            {renderPickerField('Gender', 'gender', 'male-female-outline', GENDERS, true)}
            {renderCountryField()}
            {renderCityField()}
          </View>
        );
      case 'personal':
        return (
          <View style={styles.tabContent}>
            {renderField('Spouse', 'spouse', 'heart-outline', false)}
            {renderField('Children', 'children', 'people-outline', false)}
            {renderField('Animals', 'animals', 'paw-outline', false)}
            {renderField('Hobbies', 'hobbies', 'game-controller-outline', false)}
            {renderField('Activity of Interest', 'activityOfInterest', 'star-outline', false)}
            {renderField('City of Residence', 'cityOfResidence', 'home-outline', false)}
            {renderField('Burning Desire', 'burningDesire', 'flame-outline', false)}
            {renderField('Something no one Knows About me', 'somethingNoOneKnows', 'eye-off-outline', false)}
            {renderField('Key to Successes', 'keyToSuccesses', 'key-outline', false)}
            {renderField('Personnel Goals', 'personnelGoals', 'flag-outline', false)}
            {renderField('Personnel Accomplishments', 'personnelAccomplishments', 'trophy-outline', false)}
            {renderField('Personnel Interests', 'personnelInterests', 'bulb-outline', false)}
            {renderField('Personnel Network', 'personnelNetwork', 'git-network-outline', false)}
            {renderField('Personnel Skills', 'personnelSkills', 'construct-outline', false)}
          </View>
        );
      case 'business':
        return (
          <View style={styles.tabContent}>
            {renderField('Business Name', 'businessName', 'business-outline', true)}
            {renderField('Location', 'businessLocation', 'location-outline', false)}
            {renderField('Previous Type of Jobs', 'previousTypeOfJobs', 'briefcase-outline', false)}
            {renderField('Business Goals', 'businessGoals', 'flag-outline', false)}
            {renderField('Business Accomplishments', 'businessAccomplishments', 'trophy-outline', false)}
            {renderField('Business Interests', 'businessInterests', 'bulb-outline', false)}
            {renderField('Business Network', 'businessNetwork', 'git-network-outline', false)}
            {renderField('Business Skills', 'businessSkills', 'construct-outline', false)}
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <View style={styles.wrapper}>
      {/* Sticky Tabs */}
      <View style={[styles.tabsContainer, { borderBottomColor: theme.border, backgroundColor: theme.backgroundSecondary }]}>
        {renderTabButton('contact', 'Contact')}
        {renderTabButton('personal', 'Personal')}
        {renderTabButton('business', 'Business')}
      </View>

      {/* Scrollable Content */}
      <View style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={{ flex: 1 }} {...panResponder.panHandlers}>
          <ScrollView 
            style={styles.contentContainer}
            contentContainerStyle={[
              styles.contentInner,
              {
                paddingBottom: keyboardVisible ? keyboardOffset + 32 : 32,
              },
            ]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            automaticallyAdjustKeyboardInsets={false}
            contentInsetAdjustmentBehavior="never"
          >
            {renderTabContent()}
            
            {/* Save/Cancel Buttons at Bottom */}
            {isOwnProfile && isEditing && (
              <View style={[
                styles.footerButtons, 
                { 
                  backgroundColor: theme.card,
                  paddingBottom: 16 + (insets.bottom || 0)
                }
              ]}>
                <TouchableOpacity
                  style={[
                    styles.cancelButton, 
              { 
                backgroundColor: theme.backgroundSecondary,
                borderColor: theme.border
              }
            ]}
            onPress={onCancelPress}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.cancelButtonText, { color: theme.text }]}>Cancel</ThemedText>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveButton, { backgroundColor: theme.accent }]}
            onPress={async () => {
              try {
                console.log('💾 Save pressed, photoChanged:', photoChanged, 'profilePhoto:', profilePhoto);
                
                // Validate required fields
                if (!formData.name || formData.name.trim() === '') {
                  Alert.alert('Validation Error', 'Name is required and cannot be empty.');
                  return;
                }
                
                if (!formData.designation || formData.designation.trim() === '') {
                  Alert.alert('Validation Error', 'Designation is required.');
                  return;
                }
                
                const dataToSave = { ...formData };
                
                // If photo was changed, upload it first
                if (photoChanged && profilePhoto) {
                  console.log('📸 Photo changed, checking if local URI...');
                  // Check if it's a local URI (starts with file:// or content://)
                  if (profilePhoto.startsWith('file://') || profilePhoto.startsWith('content://')) {
                    console.log('📤 Uploading local image to cloud...');
                    try {
                      const uploadedUrl = await uploadImageToServer(profilePhoto);
                      console.log('✅ Got cloud URL:', uploadedUrl);
                      (dataToSave as any).profilePhoto = uploadedUrl;
                    } catch (error) {
                      console.error('❌ Image upload failed:', error);
                      Alert.alert('Upload Error', 'Failed to upload profile photo. Please try again.');
                      return;
                    }
                  } else {
                    // It's already a URL
                    console.log('🔗 Using existing URL');
                    (dataToSave as any).profilePhoto = profilePhoto;
                  }
                } else if (photoChanged && !profilePhoto) {
                  // Photo was removed
                  console.log('🗑️ Photo removed');
                  (dataToSave as any).profilePhoto = '';
                }
                
                console.log('💾 Saving profile with data:', dataToSave);
                onSavePress(dataToSave);
              } catch (error) {
                console.error('❌ Save error:', error);
                Alert.alert('Error', 'Failed to save profile. Please try again.');
              }
            }}
            activeOpacity={0.7}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <ThemedText style={styles.saveButtonText}>Save</ThemedText>
            )}
          </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    marginHorizontal: 8,
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1.5,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    ...(Platform.OS === 'web' ? { position: 'sticky' as any, top: 0, zIndex: 10 } : {}),
  },
  tabButton: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    // Active tab styling
  },
  tabText: {
    fontSize: 15,
    fontWeight: '500',
  },
  tabTextActive: {
    fontWeight: '700',
  },
  contentContainer: {
    flex: 1,
  },
  contentInner: {
    paddingBottom: 32,
  },
  tabContent: {
    padding: 16,
  },
  emptyText: {
    fontSize: 15,
    textAlign: 'center',
  },
  profilePhotoSection: {
    alignItems: 'center',
    marginBottom: 24,
    paddingTop: 8,
  },
  largeProfilePhoto: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  profileImage: {
    width: '100%',
    height: '100%',
  },
  photoActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  photoActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  photoActionText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
  field: {
    marginBottom: 20,
  },
  fieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginLeft: 8,
    flex: 1,
    flexShrink: 1,
  },
  fieldValue: {
    fontSize: 16,
    marginLeft: 26,
  },
  textInput: {
    fontSize: 16,
    marginLeft: 26,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  pickerWrapper: {
    marginLeft: 26,
  },
  datePickerDone: {
    marginLeft: 26,
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  datePickerDoneText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default PersonalProfile;
